import { defer } from 'react-router-dom';
import {
  getPostFlowUrl,
  getReturnUrlFromRequestLoader,
} from 'utils/common/route';
import { checkUserToken } from 'utils/author';
import { getData } from 'utils/storage';
import { getWalmartAuthorizationParams } from 'hooks/router';
import storeApp from 'store/storeApp';
import { ensureUserInfoLoaded } from 'utils/loaderUtils';
import { queryBrandingNavigation } from 'hooks/useGetBranding';
import { ensureRivirTCData } from 'pages/eula/check-rivir-tc/useRivirTCQuery';

const getUserId = (userInfo) => {
  return userInfo?.id;
};

export const rivirTCLoader =
  (queryClient) =>
  async ({ request }) => {
    const returnUrl = getReturnUrlFromRequestLoader(request);
    const { walmartSearchQuery } = getWalmartAuthorizationParams(request);

    const dispatch = storeApp.dispatch;

    let userInfo = {};

    const isUserLoggedIn = checkUserToken();
    const impersonateData = getData('impersonate');

    let rivirTCData = null;
    let rivirTCError = null;

    if (isUserLoggedIn) {
      try {
        const { userInfo: loadedUserInfo } = await ensureUserInfoLoaded({
          queryClient,
          returnUrl,
        });
        userInfo = loadedUserInfo;

        const userId = getUserId(userInfo);
        const rivirTCQueryData = await ensureRivirTCData(queryClient, userId);

        if (!rivirTCQueryData) {
          rivirTCError = 'not-exist';
        } else {
          const isAccepted = rivirTCQueryData?.isAccepted;

          rivirTCData = {
            rivirTC: rivirTCQueryData,
            rivirTCContent: rivirTCQueryData.rivirTcContent,
            type: 'authorized',
          };

          const redirectUrl = getPostFlowUrl({
            type: 'check-rivir-tc',
            returnUrl,
            dispatch,
          });

          if (isAccepted) {
            const brandingPromise = queryBrandingNavigation({
              queryClient,
              userId,
            })
              .then((brandingData) => {
                return {
                  brandingData,
                  shouldNavigate: true,
                  navigationUrl: redirectUrl,
                  completed: true,
                };
              })
              .catch((error) => {
                return {
                  brandingData: null,
                  shouldNavigate: true,
                  navigationUrl: redirectUrl,
                  completed: true,
                  error: error.message,
                };
              });

            return defer({
              rivirTCData,
              rivirTCContent: rivirTCData?.rivirTCContent,
              rivirTCError,
              returnUrl,
              walmartSearchQuery,
              userInfo,
              impersonateData,
              isUserLoggedIn,
              isAccepted: true,
              isLoading: true,
              brandingPromise,
              redirectUrl,
            });
          } else {
            const brandingPromise = queryBrandingNavigation({
              queryClient,
              userId,
            })
              .then((brandingData) => {
                return {
                  brandingData,
                  shouldNavigate: false,
                  navigationUrl: null,
                  completed: true,
                };
              })
              .catch((error) => {
                return {
                  brandingData: null,
                  shouldNavigate: false,
                  navigationUrl: null,
                  completed: true,
                  error: error.message,
                };
              });

            // ✅ Return deferred data - rivirTC component shows form
            return defer({
              rivirTCData,
              rivirTCContent: rivirTCData?.rivirTCContent,
              rivirTCError,
              returnUrl,
              walmartSearchQuery,
              userInfo,
              impersonateData,
              isUserLoggedIn,
              isAccepted,
              isLoading: true,
              brandingPromise,
              redirectUrl: null, // ✅ No redirect URL for non-accepted
            });
          }
        }
      } catch (error) {
        if (error?.status === 302 || error instanceof Response) {
          throw error;
        }
        rivirTCError = 'not-exist';
      }
    }

    // Return fallback data for errors
    return {
      rivirTCData,
      rivirTCContent: null,
      rivirTCError,
      returnUrl,
      walmartSearchQuery,
      userInfo,
      impersonateData,
      isUserLoggedIn,
      isAccepted: rivirTCData?.rivirTC?.isAccepted || false,
      isLoading: false,
      brandingPromise: null,
      redirectUrl: null,
    };
  };
